/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

/**
 * Usage tracking for API calls
 * Tracks daily usage per provider to help users stay within free tier limits
 */

import { Provider } from '@/providers/llm-provider'

export interface UsageRecord {
  provider: Provider
  timestamp: number
  model: string
  tokensUsed: number
}

export interface DailyUsage {
  provider: Provider
  date: string // YYYY-MM-DD
  requestCount: number
  totalTokens: number
  records: UsageRecord[]
}

export interface ProviderLimits {
  requestsPerMinute: number
  requestsPerDay: number
  tier: 'free' | 'paid'
  warningThreshold: number // Percentage (e.g., 80 = warn at 80%)
}

// Known free tier limits
export const FREE_TIER_LIMITS: Record<string, ProviderLimits> = {
  google: {
    requestsPerMinute: 15,
    requestsPerDay: 1500,
    tier: 'free',
    warningThreshold: 80
  },
  openai: {
    requestsPerMinute: 3,
    requestsPerDay: 200,
    tier: 'free',
    warningThreshold: 80
  },
  anthropic: {
    requestsPerMinute: 5,
    requestsPerDay: 1000,
    tier: 'free',
    warningThreshold: 80
  }
}

const STORAGE_KEY = 'hivemind_usage_tracking'

/**
 * Get today's date string (YYYY-MM-DD)
 */
function getTodayString(): string {
  return new Date().toISOString().split('T')[0]
}

/**
 * Load usage data from localStorage
 */
function loadUsageData(): Record<string, DailyUsage> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    return JSON.parse(raw)
  } catch {
    return {}
  }
}

/**
 * Save usage data to localStorage
 */
function saveUsageData(data: Record<string, DailyUsage>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch (error) {
    console.error('[Usage Tracking] Failed to save:', error)
  }
}

/**
 * Track an API call
 */
export function trackApiCall(provider: Provider, model: string, tokensUsed: number = 0): void {
  try {
    const today = getTodayString()
    const key = `${provider}-${today}`

    const allData = loadUsageData()
    const existing = allData[key] || {
      provider,
      date: today,
      requestCount: 0,
      totalTokens: 0,
      records: []
    }

    const record: UsageRecord = {
      provider,
      timestamp: Date.now(),
      model,
      tokensUsed
    }

    existing.requestCount++
    existing.totalTokens += tokensUsed
    existing.records.push(record)

    allData[key] = existing
    saveUsageData(allData)
  } catch (error) {
    // Silently fail - usage tracking should never break the app
    console.warn('[Usage Tracking] Failed to track API call:', error)
  }
}

/**
 * Get today's usage for a provider
 */
export function getTodayUsage(provider: Provider): DailyUsage | null {
  const today = getTodayString()
  const key = `${provider}-${today}`
  const allData = loadUsageData()
  return allData[key] || null
}

/**
 * Get usage percentage for a provider
 */
export function getUsagePercentage(provider: Provider): { daily: number; minutely: number } {
  const usage = getTodayUsage(provider)
  if (!usage) return { daily: 0, minutely: 0 }

  const limits = FREE_TIER_LIMITS[provider]
  if (!limits) return { daily: 0, minutely: 0 }

  const dailyPercent = (usage.requestCount / limits.requestsPerDay) * 100

  // Calculate requests in last minute
  const oneMinuteAgo = Date.now() - 60000
  const recentRequests = usage.records.filter(r => r.timestamp > oneMinuteAgo).length
  const minutelyPercent = (recentRequests / limits.requestsPerMinute) * 100

  return {
    daily: Math.min(dailyPercent, 100),
    minutely: Math.min(minutelyPercent, 100)
  }
}

/**
 * Check if approaching limits
 */
export function isApproachingLimit(provider: Provider): boolean {
  const limits = FREE_TIER_LIMITS[provider]
  if (!limits) return false

  const { daily } = getUsagePercentage(provider)
  return daily >= limits.warningThreshold
}

/**
 * Get estimated time until limit reset (midnight UTC)
 */
export function getTimeUntilReset(): string {
  const now = new Date()
  const tomorrow = new Date(now)
  tomorrow.setUTCHours(24, 0, 0, 0)
  
  const diff = tomorrow.getTime() - now.getTime()
  const hours = Math.floor(diff / 3600000)
  const minutes = Math.floor((diff % 3600000) / 60000)
  
  return `${hours}h ${minutes}m`
}

